import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useDialog, useLegacyEffect } from "helper/customHook";
import { DIALOG } from "helper/enum/dialogEnum";
import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import { useDispatch, useSelector } from "react-redux";
import pxToRem from "theme/base/pxToRem";
import {
  DialogTitleCustom,
  Title,
} from "modules/PersonnelInformation/Pages/Main/component/modal/style";
import { ReactComponent as IconClose } from "assets/svg/20-close.svg";
import {
  selectArriveLateDetailData,
  selectArriveLateDetailStatus,
} from "modules/DeclareLeave/redux/selector";
import {
  getListArriveLateDetailStart,
  resetListArriveLateDetail,
} from "modules/DeclareLeave/redux/getArriveLateDetail";
import { formatDateStringToDDMMYYYY } from "helper/utils";
import {
  ApproveStatus,
  ApproveStatusValue,
  IApproveStatus,
  LeaveType,
  statusColor,
  TitleContent,
} from "../../const";
import { setupDataRequest } from "modules/DeclareLeave/redux/handleRequest";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SCREEN } from "helper/roleChecker/Screens";
import { renderContent, transformRequestDetails } from "./ArriveLateDetail";
import EmptyState from "components/common/EmptyState";
import { ReduxStateType } from "redux-store/types";
import { accountInfoDataSelector } from "layouts/Sidenav/redux/selector";
import { formReqArrivingLateLeaveEarlyReset } from "modules/DeclareLeave/redux/formReqArrivingLateLeaveEarly";
import isString from "lodash/isString";
import { useMemo } from "react";

const ArriveLateManagerDetails = () => {
  const { id: idRequest } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { employeeCode } = useSelector(accountInfoDataSelector);

  const requestLeaveDialog = useDialog(
    DIALOG.MANAGER_VIEW_REQUEST_ARRIVING_LATE_LEAVING_EARLY_DETAIL
  );
  const approveDialog = useDialog(DIALOG.APPROVE_REQUEST_POPUP);

  const open = useSelector(
    isOpenDialogSelector(
      DIALOG.MANAGER_VIEW_REQUEST_ARRIVING_LATE_LEAVING_EARLY_DETAIL
    )
  );

  const data = useSelector(selectArriveLateDetailData);
  const status = useSelector(selectArriveLateDetailStatus);

  useLegacyEffect(() => {
    if (idRequest) {
      requestLeaveDialog.open();
      dispatch(
        getListArriveLateDetailStart({
          screenCode: SCREEN.IP_017,
          id: idRequest,
        })
      );
    }
  }, [idRequest]);

  const renderBoxContent = (
    title: string,
    content: string | number,
    hasDivdiver?: boolean,
    paddingBottom: number = 1.5,
    fontWeightContent?: 400 | 500 | 600
  ) => (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      sx={(theme) => ({
        paddingBottom: paddingBottom,
        borderBottom: hasDivdiver
          ? `1px solid ${theme.palette.stroke.strokeDefault2}`
          : "none",
        marginBottom: hasDivdiver ? 1.5 : 0,
      })}
    >
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.size.sm_14,
          color: theme.palette.text.textSecondary,
          fontWeight: theme.typography.fw_regular_400,
          width: "40%",
        })}
      >
        {title}
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.size.sm_14,
          color:
            title === TitleContent.status
              ? statusColor(theme, content as IApproveStatus, "color")
              : theme.palette.text.textPrimary,
          fontWeight: fontWeightContent ?? theme.typography.fw_regular_400,
          flex: 1,
          textAlign: "right",
        })}
      >
        {title === TitleContent.status
          ? ApproveStatus[content]
          : (content ?? "---")}
      </Typography>
    </Box>
  );

  const renderLeaveDetails = () => {
    if (!data?.details || data?.details.length === 0) {
      return null;
    }
    const requests = transformRequestDetails(data?.details ?? []);
    if (data?.type === LeaveType.LONG_TERM) {
      return renderContent(
        1,
        false,
        `Từ ${formatDateStringToDDMMYYYY(requests[0].fromDate)} đến ${formatDateStringToDDMMYYYY(requests[requests.length - 1].toDate)}`,
        requests[0].type,
        requests[0].reason
      );
    }
    return requests.map((leave, index) => {
      const hasBorderBottom = index < requests.length - 1;
      return renderContent(
        index,
        hasBorderBottom,
        `Ngày ${formatDateStringToDDMMYYYY(leave.fromDate)}`,
        leave.type,
        leave.reason
      );
    });
  };

  const isDisableAction = useMemo(() => {
    if (data?.status !== ApproveStatusValue.PENDING) return true;
    if (data && data.approveCode === employeeCode) return false;
    return (
      data.employeeCode === employeeCode ||
      (data.relatedPersons ?? [])
        .map((item) => item.value)
        .includes(employeeCode)
    );
  }, [data, employeeCode]);

  const onClickApprove = (isApprove: boolean) => {
    handleClose();
    dispatch(setupDataRequest({ isApprove, id: data?.id }));
    approveDialog.open();
  };

  const handleClose = () => {
    requestLeaveDialog.close();
    const newURL = pathname.replace(`/${idRequest}`, "");
    navigate(newURL, { replace: true });
    dispatch(formReqArrivingLateLeaveEarlyReset());
    dispatch(resetListArriveLateDetail());
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitleCustom>
        <Title>Chi tiết đơn xin đi muộn/về sớm</Title>
        <IconButton onClick={handleClose}>
          <IconClose />
        </IconButton>
      </DialogTitleCustom>
      {isString(data) && status !== ReduxStateType.INIT && (
        <DialogContent>
          <EmptyState message={data} />
        </DialogContent>
      )}
      {status === ReduxStateType.SUCCESS && (
        <>
          <DialogContent
            sx={(theme) => ({
              background: theme.palette.background.bgTertiary,
              p: 0,
            })}
          >
            <Box
              sx={(theme) => ({
                margin: 2,

                padding: pxToRem(20),
                paddingBottom: 1,
                background: "white",
                borderRadius: pxToRem(8),
              })}
            >
              {renderBoxContent(
                TitleContent.createDate,
                formatDateStringToDDMMYYYY(data?.registerDate)
              )}
              {renderBoxContent(
                TitleContent.employeeName,
                data?.employeeName,
                false,
                0
              )}
              {renderBoxContent(
                "",
                `${data?.positionName} - ${data?.departmentName}`
              )}

              {/* Leave Details */}
              <Box mt={1} mb={pxToRem(20)}>
                <Box
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.stroke.strokeDefault}`,
                    borderRadius: pxToRem(8),
                    paddingX: 2,
                    paddingY: 0.5,
                  })}
                >
                  {renderLeaveDetails()}
                </Box>
              </Box>
              {/* Leave Summary */}

              {renderBoxContent(
                TitleContent.totalLackOfTime,
                data?.details.length
              )}
              {renderBoxContent(TitleContent.note, data?.note)}
              {renderBoxContent(
                `${TitleContent.relatedPerson} ${data?.relatedPersons.length > 1 ? `(${data.relatedPersons.length})` : ""}`,
                (data?.relatedPersons ?? [])
                  .map((person) => person.label)
                  .join(", "),
                true
              )}

              {/* Approval Process */}
              {renderBoxContent(TitleContent.approval, data?.approveName)}
              {renderBoxContent(
                TitleContent.status,
                data?.status,
                false,
                1.5,
                600
              )}
              {data?.refuseReason &&
                renderBoxContent(
                  TitleContent.approveReason,
                  data?.refuseReason ?? "---"
                )}
              {renderBoxContent(
                TitleContent.approvalDate,
                data?.approveDate
                  ? formatDateStringToDDMMYYYY(data?.approveDate)
                  : "---"
              )}
            </Box>
          </DialogContent>

          {/* Actions */}
          <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
            <Box display="flex" width={"100%"}>
              <Button
                fullWidth
                variant="blur"
                onClick={handleClose}
                sx={(theme) => ({
                  mr: 1,
                  background: theme.palette.background.bgSecondary,
                  color: theme.palette.text.textPrimary,
                })}
              >
                Đóng
              </Button>

              <Button
                color="success"
                fullWidth
                sx={{ mr: 1, gap: 1 }}
                disabled={isDisableAction}
                onClick={() => onClickApprove(true)}
              >
                Phê duyệt
              </Button>
              <Button
                color="error"
                fullWidth
                sx={{ gap: 1 }}
                disabled={isDisableAction}
                onClick={() => onClickApprove(false)}
              >
                Từ chối
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ArriveLateManagerDetails;
